import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, FormArray } from '@angular/forms';
import * as moment from 'moment';
import { LoaderService } from 'src/app/loader.service';
import { AlertDialogService } from 'src/app/shared/alert-dialog/alert-dialog.service';
import { SharedService } from 'src/app/shared/shared.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';
import { SubscriptionService } from '../../subscription.service';
import { planTypeFromConfig } from '../../subscription.applicable-types';
import { subscriptionAccessFeeCreateAlert, subscriptionOperationFailureAlert } from '../../subscription.create-alert';

export interface AccessFeeContext {
  city: { id: string, name: string };
  mode: { id: string, name: string };
  category?: { id: string, name: string };
  service?: { id: string, name: string, serviceDetailId?: string, geoServiceGroupId?: string, mode: string };
}

@Component({
  selector: 'app-create-accessfee',
  templateUrl: './accessFee.create.component.html',
  styleUrls: ['./accessFee.create.component.css']
})
export class CreateAccessFeeComponent implements OnInit {
  public accessFeeFormGroup = new FormGroup({
    planName: new FormControl(null),
    city: new FormControl(null),
    services: new FormControl(null),
    zones: new FormControl(null),
    userSelector: new FormControl(null),
    targetSegment: new FormControl('userSelectors'),
    priority: new FormControl(null),
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    tiers: new FormControl([])
  });

  @Input() context: AccessFeeContext = null;
  @Input() launch: { city: string, service: string } = null;
  @Output() launchServiceUnavailable: EventEmitter<void> = new EventEmitter<void>();
  @Output() launchConsumed: EventEmitter<void> = new EventEmitter<void>();

  public BillingPlanResponse : {
    rejectedPlans: string[];
    reason: string;
  } ;


  public cities: { id: string, name: string; }[] = [];
  public zones: { id: string, name: string; }[] = [];
  public services: {
    id: string,
    name: string,
    mode: string,
    serviceDetailId: string,
    categoryId: string,
    geoServiceGroupId: string;
  }[] = [];


  public userSelectors: { id: string, name: string; }[] = [];
  public deductionCondition = 'Days';
  public daysCondition = '1';

  public today: Date = moment().startOf('day').toDate();
  public tomorrowEod: Date = moment().endOf('day').add(1, 'days').toDate();
  public maxStartDate: Date;
  public minEndDate: Date = this.tomorrowEod;


  private initReady: Promise<any>;

  constructor(private sharedService: SharedService,
    private subsService: SubscriptionService,
    private loader: LoaderService,
    private alertService: AlertDialogService,
    public toasterService: ToasterService) {
    this.initializeCities();
    this.addTier();
  }

  private initializeCities() {
    this.loader.openLoading();
    this.initReady = this.fetchCities().finally(() => this.loader.closeLoading());
  }

  private async applyLaunchQueryPrefill() {
    const q = this.launch || { city: '', service: '' };
    const cityId = (q.city || '').trim();
    const serviceId = (q.service || '').trim();
    if (!cityId) {
      return;
    }

    try {
      await this.initReady;
      const city = this.cities.find(c => String(c.id) === String(cityId));
      if (!city) {
        this.errorMessage('City from launch URL is not available.');
        this.launchConsumed.emit();
        return;
      }

      this.accessFeeFormGroup.get('city').setValue(city, { emitEvent: false });
      await this.fetchServices();
      if (!serviceId) {
        this.launchConsumed.emit();
        return;
      }
      const matched = (this.services || []).filter(s => String(s.id) === String(serviceId));
      if (!matched.length) {
        this.launchServiceUnavailable.emit();
        return;
      }
      this.accessFeeFormGroup.get('services').setValue(matched);
      this.launchConsumed.emit();
    } catch (err) {
      console.error('Launch prefill failed', err);
      this.launchConsumed.emit();
    }
  }

  resetOnchangeOf(controlName: string, toReset: string[], cb = () => { }) {
    this.accessFeeFormGroup.get(controlName).valueChanges.subscribe(() => {
      toReset.forEach(control => {
        this.accessFeeFormGroup.get(control).setValue(null);
      });
      cb();
    });
  }

  async ngOnInit(): Promise<void> {
    this.resetOnchangeOf("city", ["services", "zones", "userSelector", "tiers", "startDate", "endDate", "priority"], () => {
      this.services = [];
      this.userSelectors = [];
      try {
        this.addTier();
        this.fetchServices();
      } catch (err) {
        console.error(err);
      }
    });

    this.resetOnchangeOf("services", ["zones", "userSelector", "tiers", "startDate", "endDate", "priority"], () => {
      this.userSelectors = [];
      this.zones = [];
      try {
        this.addTier();
        this.fetchZones();
        this.fetchSelectors();
        this.validateModeAndGeoGroupIdForServices() as string;
      } catch (err) {
        console.error(err);
      }
    });


    this.resetOnchangeOf("zones", ["userSelector", "tiers", "startDate", "endDate", "priority"], () => {
      this.addTier();
    })

    this.accessFeeFormGroup.get('targetSegment').valueChanges.subscribe(() => {
      this.applyTargetSegmentFields();
    });
    this.applyTargetSegmentFields();

    if (this.context) {
      await this.applyParentContext();
    } else {
      await this.applyLaunchQueryPrefill();
    }
  }

  private applyTargetSegmentFields() {
    const isUserSelector = this.formValue('targetSegment') === 'userSelectors';
    ['endDate', 'priority', 'userSelector'].forEach(name => {
      const control = this.accessFeeFormGroup.get(name);
      if (isUserSelector) {
        control.enable({ emitEvent: false });
        return;
      }
      control.setValue(null, { emitEvent: false });
      control.disable({ emitEvent: false });
    });
  }

  compareServices = (a, b) => a && b && String(a.id) === String(b.id);

  blockInvalidNumberKeys(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  rejectNegativeAmount(event: Event, control) {
    const input = event.target as HTMLInputElement;
    if (input.value === '' || input.value == null) {
      return;
    }
    if (Number(input.value) < 0) {
      control.setValue(null);
      input.value = '';
    }
  }

  private async applyParentContext() {
    await this.initReady;
    this.accessFeeFormGroup.get('city').setValue(this.context.city, { emitEvent: false });
    await this.fetchServices();
    const parentService = this.context.service;
    if (!parentService) {
      return;
    }
    let matched = (this.services || []).filter(s => String(s.id) === String(parentService.id));
    if (!matched.length) {
      const injected = {
        id: parentService.id,
        name: parentService.name,
        mode: (this.context.mode && this.context.mode.id) || parentService.mode,
        serviceDetailId: parentService.serviceDetailId,
        geoServiceGroupId: parentService.geoServiceGroupId || '',
        categoryId: this.context.category && this.context.category.id
      };
      this.services = [injected].concat(this.services || []);
      matched = [injected];
    }
    this.accessFeeFormGroup.get('services').setValue(matched, { emitEvent: false });
    this.fetchZones();
    this.fetchSelectors();
  }

  fetchSelectors() {
    const city = this.formValue("city");
    const services = this.formValue("services");
    if (city && services) {
      const serviceTypes = services
        .map(s => s.serviceDetailId).join(",");
      this.loader.openLoading();
      this.sharedService
        .fetchRules(city.id, serviceTypes, null, "rider", "custom")
        .toPromise()
        .then((res: any) => {
          this.userSelectors = res.data.selectors
            .map(({ _id, name }) => ({ id: _id, name }));
        })
        .finally(() => this.loader.closeLoading());
    }
  }

  formValue(name) {
    return this.accessFeeFormGroup.get(name).value;
  }

  fetchZones() {
    const city = this.formValue("city");
    const services = this.formValue("services") || [];
    const geoGroupIds = {};
    services.forEach(service => {
      if (service.geoServiceGroupId) {
        geoGroupIds[service.geoServiceGroupId] = true;
      }
    });
    const groupIds = Object.keys(geoGroupIds);
    if (!city || !groupIds.length) {
      this.zones = [];
      return Promise.resolve();
    }
    this.loader.openLoading();
    return Promise.all(groupIds.map(groupId =>
      this.sharedService.fetchZones(city.id, groupId).toPromise()
    ))
      .then((responses: any[]) => {
        const seen = {};
        this.zones = [];
        responses.forEach(res => {
          ((res && res.geolayers) || []).forEach(({ id, name }) => {
            if (seen[id]) {
              return;
            }
            seen[id] = true;
            this.zones.push({ id, name });
          });
        });
      })
      .finally(() => this.loader.closeLoading());
  }


  private fetchServices(): Promise<void> {
    const city = this.formValue("city");
    if (!city) {
      return Promise.resolve();
    }
    this.loader.openLoading();
    return this.sharedService
      .fetchServicesWithMode(city.id)
      .toPromise()
      .then((res: any) => {
        const parentCategoryId = this.context && this.context.category && this.context.category.id;
        const mapped = res.data
          .filter(serviceDetail => serviceDetail.active)
          .filter(({ modeInfo }) => {
            return !this.context || !this.context.mode
              || String(modeInfo._id) === String(this.context.mode.id);
          })
          .map(({ service, _id, modeInfo, category, serviceCategory, categoryName }) => {
            const rawCategory = (category && (category._id || category.id || category.name))
              || (serviceCategory && (serviceCategory._id || serviceCategory.id || serviceCategory.name))
              || (service && service.category && (service.category._id || service.category.id || service.category.name))
              || (service && service.categoryName)
              || categoryName;
            return {
              id: service._id,
              name: service.name,
              mode: modeInfo._id,
              serviceDetailId: _id,
              geoServiceGroupId: service.geoServiceGroupId,
              categoryId: rawCategory ? String(rawCategory) : parentCategoryId
            };
          });
        const sdids = mapped.map(item => item.serviceDetailId).filter(Boolean);
        if (!sdids.length) {
          this.services = [];
          return;
        }
        const modeId = (this.context && this.context.mode && this.context.mode.id) || (mapped[0] && mapped[0].mode);
        return this.subsService.getApplicableTypes(sdids, modeId).toPromise().then((configsRes: any) => {
          const configs = (configsRes && configsRes.data) || {};
          this.services = mapped.filter(item => planTypeFromConfig(configs[item.serviceDetailId]) === 'accessFee');
        });
      })
      .finally(() => this.loader.closeLoading());
  }

  private fetchCities(): Promise<void> {
    return this.sharedService
      .fetchCities()
      .toPromise()
      .then((res: any) => {
        this.cities = res.data.cities
          .map(({ _id, displayName }) => ({ id: _id, name: displayName }));
      });
  }

  changeInDate() {
    const startDate = this.formValue("startDate");

    if (startDate) {
      this.minEndDate = startDate;
    }

    const endDate = this.formValue("endDate");
    if (endDate) {
      this.maxStartDate = endDate;
    } else {
      this.maxStartDate = null;
    }
  }

  async createAccessFeeRules() {
    this.loader.openLoading();

    try {
      const plans = await this.buildBillingPlan();
      if (!plans) {
        return;
      }

      this.subsService.createBillingPlan({ plans }).subscribe(
        (resp: any) => {
          this.alertService.open(subscriptionAccessFeeCreateAlert(resp));
        },
        (error) => {
          this.alertService.open(subscriptionOperationFailureAlert(error));
        }
      );

    } catch (error) {
      this.alertService.open(subscriptionOperationFailureAlert(error));
    } finally {
      this.loader.closeLoading();
    }
  }

  alertMessage(alert: { title: string, message: string }) {
    this.alertService.open(alert)
  }

  errorMessage(message) {
    this.toasterService.showToaster(new Toaster({
      type: ToasterType.WARNING,
      message: message
    }));
  }

  validateField(fieldName: string, message: string) {
    const value = this.formValue(fieldName);
    if (!value) {
      this.errorMessage(message);
      throw new Error(message);
    }
    return value;
  }


  async buildBillingPlan() {
    const planName = this.validateField("planName", "Plan name is required");
    const city = this.validateField("city", "City is required");
    const services = this.validateField("services", "Services is required");
    const mode: string = this.validateModeAndGeoGroupIdForServices() as string;
    const zones = this.formValue("zones");
    const startDate = this.validateField("startDate", "Purchase Start date is required");
    let endDate = this.formValue("endDate"); 
    let priority, userSelector;

    if(this.accessFeeFormGroup.get('targetSegment').value === 'userSelectors') {

      priority =  this.formValue("priority");
      if (!priority || !Number.isInteger(priority) || priority <= 0 )  {
        this.errorMessage("Priority is required/should be positive number for userSelector");
        throw new Error("Priority is required/should be positive number for userSelector");
      }

      userSelector = this.formValue("userSelector");
      if (!userSelector)  {
        this.errorMessage("UserSelector is required for targetSegment userSelector");
        throw new Error("UserSelector is required for targetSegment userSelector");
      }
    }else {
      if (this.formValue("endDate")) {
        this.errorMessage("End date cannot be set for plans without user selectors");
        throw new Error("End date cannot be set for plans without user selectors");
      }

    }

    if (this.formValue('tiers').length < 1) {
      this.errorMessage("At least one tier is required");
    }

    if(this.formValue('tiers').length>1) {
      let minRides = this.formValue('tiers').map(tier => tier.minRides.value)
      for (let i = 1; i < minRides.length; i++) {
        if (minRides[i] <= minRides[i - 1]) {
          this.errorMessage("Minimum rides should be in increasing order");
          throw new Error("Minimum rides should be in increasing order");
        }
      }
    }

    let commonFields = {
      planName,
      startDate: moment(startDate).format("YYYY-MM-DD"),
      priority: Number(priority),
      city: city.id,
      services: services.map(s => s.id),
      zones: zones && zones.length > 0 ? zones.map(z => z.id) : undefined,
      mode,
      userSelectors: userSelector ? userSelector.map(usId => usId.id) : undefined,
    };


    return [{
      ...commonFields,
      ...(this.accessFeeFormGroup.get('targetSegment').value === 'userSelectors'&& endDate
        ? { endDate: moment(endDate).format("YYYY-MM-DD") }
        : {}
      ),
      tiers: this.formValue('tiers').map((tier, index) => {
        this.validateTier(tier, index);
        return {
          minValue: tier.minRides.value,
          amount: tier.tierAmount.value,
          strikeOutAmount: tier.strikeOutAmount.value
        };
      })
    }];

  }

  validateTier(tier, index) {

    if (!tier.minRides || !Number.isInteger(tier.minRides.value) || tier.minRides.value <= 0 ) {
      this.errorMessage(`minimum rides value is required for tier ${index + 1}`);
      throw new Error(`Minimum rides value is required/ should be non zero positive number for tier ${index + 1}`);
    }

    if (!tier.tierAmount || (!Number.isInteger(tier.tierAmount.value) || tier.tierAmount.value < 0)) {
      this.errorMessage(`Charged Fee is required and should be integer for tier  ${index + 1}`);
      throw new Error(`Charged Fee is required and should be non negative number for tier ${index + 1}`);
    }

    if (tier.strikeOutAmount && (tier.strikeOutAmount.value || tier.strikeOutAmount.value == 0) &&
      (!Number.isInteger(tier.strikeOutAmount.value) || tier.strikeOutAmount.value < 0 || tier.strikeOutAmount.value <= tier.tierAmount.value)) {
      this.errorMessage(`Striked Out amount should be positive number for tier ${index + 1}`);
      throw new Error(`Striked Out should be greater than charged fee for tier ${index + 1}`);
    }
  }

  addTier() {
    let defaultTierElement = {
        tierCondition : new FormControl(null),
        tierAmount: new FormControl(null),
        minRides: new FormControl(null),
        strikeOutAmount: new FormControl(null),
      }
      if (this.formValue('tiers')) {
        this.formValue('tiers').push(defaultTierElement)
      } else {
        this.accessFeeFormGroup.get('tiers').setValue([defaultTierElement])
      }
  }

  deleteTier(index) {
    this.formValue('tiers').splice(index, 1)
  }

  validateModeAndGeoGroupIdForServices() {
    if (!this.formValue('services')) {
      return
    }
    let distinctModes = new Set(this.formValue('services').map((service: { mode: string }) => service.mode))

    if ( distinctModes.size > 1 ) {
      this.accessFeeFormGroup.get('services').setValue(null)
      this.alertMessage({
        title: "Error",
        message: "Please select services with same mode"
      });
      return;
    }

    let mode = distinctModes.values().next().value;
    return mode;
  }

}
